// MIDAS V3 — the quiet lines under a Variant E status table
// (MIDAS-ANSWER-FORMAT-1 r5b / r5c). Split out of statusTableGrammar.ts.
//
// Fixed forms confirmed by the bot for revision c-6 (MACHINEID_TODO.md
// 2026-10-04, "round 5 / r5b answers", r5b answer 4). Both are composed by the
// bot's loop, never model text:
//
// 1. The "N cannot be verified" sentence: the paragraph right after the
//    table, sent only when N > 0, with numeral agreement:
//      sk  N podmienka sa teraz na stroji nedá overiť.        (N = 1)
//          N podmienky sa teraz na stroji nedajú overiť.      (2–4)
//          N podmienok sa teraz na stroji nedá overiť.        (5+)
//      en  N condition cannot be verified on the machine right now.
//          N conditions cannot be verified on the machine right now.
//      cs  N podmínka se teď na stroji nedá ověřit.
//          N podmínky se teď na stroji nedají ověřit.
//          N podmínek se teď na stroji nedá ověřit.
//    This is a PHRASE test, not a whole-sentence match, so the earlier
//    prompt-variant wordings ("nelze ověřit", "could not be verified") still
//    match.
// 2. An optional second fixed paragraph, quiet only directly after (1):
//      sk  Stačia červené riadky — výstup <address> je blokovaný bez ohľadu na neoverené.
//      en  The red rows suffice — output <address> is blocked regardless of what is unverified.
//      cs  Stačí červené řádky — výstup <address> je blokován bez ohledu na neověřené.
//    This one is a whole-sentence match; only the address varies.
//
// Pure — no React, no DOM.

const UNVERIFIABLE_RE = new RegExp(
  [
    // sk: nedá (1 and 5+) / nedajú (2–4)
    "nedaj[uú]\\s+overi[tť]",
    "ned[aá]\\s+overi[tť]",
    "nepodarilo\\s+(?:sa\\s+)?overi[tť]",
    "nebolo\\s+mo[zž]n[eé]\\s+overi[tť]",
    "nemo[zž]no\\s+overi[tť]",
    // cs: nedá (1 and 5+) / nedají (2–4)
    "nelze\\s+ov[eě][rř]it",
    "nedaj[ií]\\s+ov[eě][rř]it",
    "ned[aá]\\s+ov[eě][rř]it",
    "nepoda[rř]ilo\\s+(?:se\\s+)?ov[eě][rř]it",
    // en
    "(?:cannot|can't|can\\s+not|could\\s+not|couldn't)\\s+(?:\\w+\\s+)?be\\s+verified",
    "unverifiable",
  ].join("|"),
  "i"
);

// "<prefix> — <output address> <suffix>". The address is free text (it may
// even hold a chip sentinel); any dash and spacing around it is tolerated.
const RED_ROWS_SUFFICE_RE = new RegExp(
  "^(?:" +
    [
      "Stačia červené riadky\\s*[—–-]\\s*výstup\\s+.+?\\s+je blokovaný bez ohľadu na neoverené",
      "The red rows suffice\\s*[—–-]\\s*output\\s+.+?\\s+is blocked regardless of what is unverified",
      "Stačí červené řádky\\s*[—–-]\\s*výstup\\s+.+?\\s+je blokován bez ohledu na neověřené",
    ].join("|") +
    ")\\s*\\.?$",
  "i"
);

/** Longer than this is prose, not one of the quiet sentences. */
export const QUIET_LINE_MAX_CHARS = 280;

// NFC first: a decomposed "ť" (t + combining caron) must match like the
// composed one. Whitespace runs (incl. a soft line break) collapse to one space.
const normalise = (text: string): string =>
  (text || "").normalize("NFC").replace(/\s+/g, " ").trim();

/** The "N … cannot be verified" sentence (sk / en / cs). */
export const isUnverifiableSentence = (text: string): boolean => {
  const t = normalise(text);
  return t.length > 0 && t.length <= QUIET_LINE_MAX_CHARS && UNVERIFIABLE_RE.test(t);
};

/** The optional "red rows suffice — output … is blocked" paragraph. */
export const isRedRowsSufficeSentence = (text: string): boolean => {
  const t = normalise(text);
  return t.length > 0 && t.length <= QUIET_LINE_MAX_CHARS && RED_ROWS_SUFFICE_RE.test(t);
};
